import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { DEFAULT_CATEGORIES } from '../lib/defaults'
import MarkupPanel from './MarkupPanel'
import '../styles.css'

const fmt = (n) => `£${Number(n || 0).toFixed(2)}`
const gbp = n => '£' + Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const today = () => new Date().toISOString().slice(0, 10)
const formatDate = (iso) => {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
}

// Map category ids to CSS custom property tokens
const CAT_COLOR = {
  timber:     'var(--c-timber)',
  fixings:    'var(--c-fixings)',
  adhesives:  'var(--c-adhesives)',
  site:       'var(--c-site)',
  additional: 'var(--c-additional)',
  labour:     'var(--c-site)',
}

export default function QuoteScreen({ session }) {
  const { id } = useParams()
  const navigate = useNavigate()

  // ── project data ──────────────────────────────────────────────────────────────
  const [project, setProject] = useState(null)
  const [lineItems, setLineItems] = useState([])
  const [labourLines, setLabourLines] = useState([])
  const [timeTasks, setTimeTasks] = useState([])
  const [settings, setSettings] = useState(null)

  // ── quote state ───────────────────────────────────────────────────────────────
  const [quote, setQuote] = useState(null)
  const [lineStates, setLineStates] = useState({})   // { id: 'show'|'hide' }
  const [dateIssued, setDateIssued] = useState(today())
  const [quoteRef, setQuoteRef] = useState('')
  const [description, setDescription] = useState('')
  const [paymentTerms, setPaymentTerms] = useState('')
  const [quoteNotes, setQuoteNotes] = useState('')

  // ── ui ────────────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [savedMsg, setSavedMsg] = useState(false)
  const [hintDismissed, setHintDismissed] = useState(
    () => localStorage.getItem('cp-hint-dismissed') === '1'
  )

  // ── load ──────────────────────────────────────────────────────────────────────
  useEffect(() => {
    Promise.all([loadProject(), loadSettings(), loadQuote()])
      .then(() => setLoading(false))
  }, [id])

  const loadProject = async () => {
    const { data: proj } = await supabase.from('projects').select('*').eq('id', id).single()
    setProject(proj)
    const { data: items } = await supabase.from('line_items').select('*').eq('project_id', id).order('sort_order')
    setLineItems(items || [])
    const { data: labour } = await supabase.from('labour_lines').select('*').eq('project_id', id).order('sort_order')
    setLabourLines(labour || [])
    const { data: tasks } = await supabase.from('time_tasks').select('*').eq('project_id', id).order('sort_order')
    setTimeTasks(tasks || [])
  }

  const loadSettings = async () => {
    const { data } = await supabase.from('settings').select('*').eq('user_id', session.user.id).single()
    setSettings(data)
  }

  const loadQuote = async () => {
    const { data } = await supabase.from('quotes').select('*')
      .eq('project_id', id).order('created_at', { ascending: false }).limit(1).single()
    if (data) {
      setQuote(data)
      setDateIssued(data.date_issued || today())
      setQuoteRef(data.quote_ref || '')
      setDescription(data.description || '')
      setPaymentTerms(data.payment_terms || '')
      setQuoteNotes(data.notes || '')
    }
    return data
  }

  const initialised = useRef(false)

  // ── compute initial line states ────────────────────────────────────────────────
  useEffect(() => {
    if (loading) return
    if (lineItems.length === 0 && labourLines.length === 0 && timeTasks.length === 0) return
    if (initialised.current) return
    initialised.current = true
    const defaults = {}
    lineItems.forEach(item => {
      defaults[item.id] = ((item.unit_cost || 0) * (item.quantity || 0) === 0) ? 'hide' : 'show'
    })
    labourLines.forEach(item => {
      const days = item.from_estimator
        ? timeTasks.reduce((s, t) => s + (t.unit_duration || 0), 0)
        : (item.days || 0)
      defaults[item.id] = days === 0 ? 'hide' : 'show'
    })
    // Orphan B&I virtual item
    const hasFromEst = labourLines.some(l => l.from_estimator)
    if (!hasFromEst && timeTasks.length > 0) {
      const biDays = timeTasks.reduce((s, t) => s + (t.unit_duration || 0), 0) / (project?.hours_per_day || 8)
      defaults['__bi__'] = biDays > 0 ? 'show' : 'hide'
    }
    const saved = quote?.line_states || {}
    const merged = { ...defaults }
    Object.keys(saved).forEach(k => {
      // Only respect saved state if the item has cost — never override zero-cost hide
      if (merged[k] !== undefined && merged[k] !== 'hide') merged[k] = saved[k]
    })
    setLineStates(merged)
  }, [loading, lineItems, labourLines, timeTasks])

  // ── derived ───────────────────────────────────────────────────────────────────
  const dayRate = project?.day_rate || 350
  const hoursPerDay = project?.hours_per_day || 8

  // Guard against duplicate from_estimator rows (data anomaly from old auto-stamp code)
  const deduplicatedLabourLines = labourLines.filter((l, _, arr) =>
    !l.from_estimator || arr.findIndex(m => m.from_estimator) === arr.indexOf(l)
  )

  // Orphan B&I: B&I tasks exist but no from_estimator labour line — treat as virtual item
  const biTotalDays = timeTasks.reduce((s, t) => s + (t.unit_duration || 0), 0) / hoursPerDay
  const hasFromEstimator = deduplicatedLabourLines.some(l => l.from_estimator)
  const orphanDays = !hasFromEstimator ? biTotalDays : 0
  const orphanBiItem = orphanDays > 0
    ? { id: '__bi__', name: 'Build & Install', _isOrphanBi: true, _days: orphanDays }
    : null

  const catItemsMap = (catId) => {
    if (catId === 'labour') return [...deduplicatedLabourLines, ...(orphanBiItem ? [orphanBiItem] : [])]
    return lineItems.filter(i => i.category === catId)
  }

  const itemCost = (item, catId) => {
    if (catId === 'labour') {
      if (item._isOrphanBi) return item._days * dayRate
      if (item.from_estimator) return (timeTasks.reduce((s, t) => s + (t.unit_duration || 0), 0) / hoursPerDay) * dayRate
      return (item.days || 0) * dayRate
    }
    return (item.quantity || 0) * (item.unit_cost || 0)
  }

  const itemQtyLabel = (item, catId) => {
    if (catId === 'labour') {
      const days = item._isOrphanBi
        ? item._days
        : item.from_estimator
          ? timeTasks.reduce((s, t) => s + (t.unit_duration || 0), 0) / hoursPerDay
          : (item.days || 0)
      return `${days.toFixed(2)} day${days === 1 ? '' : 's'}`
    }
    const qty = item.quantity ?? 1
    return `${qty} × £${Number(item.unit_cost || 0).toFixed(2)}`
  }

  const getMarkup = (catId) => {
    if (!project) return 0
    return catId === 'labour'
      ? (project.labour_markup_pct ?? 0)
      : (project.material_markup_pct ?? 20)
  }

  const catRawTotal = (catId) => catItemsMap(catId).reduce((s, item) => s + itemCost(item, catId), 0)

  const catClientTotal = (catId) => catRawTotal(catId) * (1 + getMarkup(catId) / 100)

  const isHidden = (item) => lineStates[item.id] === 'hide'
  const toggleItem = (item) => {
    setLineStates(s => ({ ...s, [item.id]: isHidden(item) ? 'show' : 'hide' }))
  }

  const visibleItems = (catId) => catItemsMap(catId).filter(i => !isHidden(i))
  const hiddenItems = (catId) => catItemsMap(catId).filter(i => isHidden(i))
  const allHidden = (catId) => {
    const items = catItemsMap(catId).filter(i => itemCost(i, catId) > 0)
    return items.length > 0 && items.every(i => isHidden(i))
  }
  const sundryClientTotal = (catId) => {
    const mu = getMarkup(catId)
    return hiddenItems(catId)
      .filter(i => itemCost(i, catId) > 0)
      .reduce((s, i) => s + itemCost(i, catId), 0) * (1 + mu / 100)
  }

  const clientSubtotal = DEFAULT_CATEGORIES.reduce((s, c) => s + catClientTotal(c.id), 0)
  const vatAmt = project?.vat_registered ? clientSubtotal * ((project?.vat_rate ?? 20) / 100) : 0
  const grandTotal = clientSubtotal + vatAmt

  // ── update project fields inline ──────────────────────────────────────────────
  const updateProjectField = async (field, value) => {
    setProject(p => ({ ...p, [field]: value }))
    await supabase.from('projects').update({ [field]: value }).eq('id', id)
  }

  const updateMarkup = (field, value, save) => {
    if (field === 'vat_registered') {
      updateProjectField('vat_registered', value)
    } else {
      const parsed = parseFloat(value)
      const toSave = isNaN(parsed) ? 0 : parsed
      setProject(p => ({ ...p, [field]: toSave }))
      if (save) updateProjectField(field, toSave)
    }
  }

  // ── save ──────────────────────────────────────────────────────────────────────
  const saveQuote = async () => {
    setSaving(true)
    const payload = {
      project_id: id,
      user_id: session.user.id,
      line_states: lineStates,
      date_issued: dateIssued,
      quote_ref: quoteRef,
      description,
      payment_terms: paymentTerms,
      notes: quoteNotes,
    }
    let error
    if (quote) {
      ;({ error } = await supabase.from('quotes')
        .update({ ...payload, updated_at: new Date().toISOString() })
        .eq('id', quote.id))
    } else {
      const { data, error: e } = await supabase.from('quotes').insert(payload).select().single()
      error = e; if (data) setQuote(data)
    }
    setSaving(false)
    if (!error) { setSavedMsg(true); setTimeout(() => setSavedMsg(false), 2000) }
  }

  const printPdf = async () => {
    await saveQuote()
    window.print()
  }

  // ── render ────────────────────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '70vh', fontFamily: 'var(--mono)', fontSize: '0.75rem', color: 'var(--ink-3)' }}>
      Loading…
    </div>
  )

  const tradingName = settings?.trading_name || ''
  const cats = DEFAULT_CATEGORIES.filter(c => {
    const items = catItemsMap(c.id)
    if (items.length === 0) return false
    // Only show category if at least one item has cost
    return items.some(i => itemCost(i, c.id) > 0)
  })

  return (
    <>
      <style>{`
        @media print {
          #cp-screen { display: none !important; }
          #appbar { display: none !important; }
          #cp-print { display: block !important; }
          @page { margin: 15mm; size: A4; }
        }
        #cp-print { display: none; }
        .cp-editable:hover { outline: 1.5px solid var(--green); outline-offset: 2px; border-radius: 2px; }
        .cp-editable:focus { outline: 1.5px solid var(--green); outline-offset: 2px; border-radius: 2px; }
        .cp-line-row { cursor: pointer; }
        .cp-line-row:hover { background: var(--surface-2); border-radius: 3px; }
        .cp-meta-input {
          border: none;
          background: transparent;
          font-family: inherit;
          font-size: 0.82rem;
          text-align: right;
          outline: none;
          width: 140px;
          padding: 1px 2px;
          cursor: text;
          color: var(--ink);
        }
        .cp-meta-textarea {
          border: none;
          background: transparent;
          font-family: inherit;
          font-size: 0.82rem;
          text-align: right;
          outline: none;
          width: 140px;
          padding: 1px 2px;
          resize: none;
          color: var(--ink);
          line-height: 1.5;
        }
        .cp-description-area {
          width: 100%;
          border: none;
          font-family: inherit;
          font-size: 0.85rem;
          line-height: 1.6;
          color: var(--ink);
          background: transparent;
          outline: none;
          resize: none;
          padding: 0.5rem 0;
          margin-bottom: 0.5rem;
          box-sizing: border-box;
        }
        .cp-text-area {
          width: 100%;
          border: none;
          font-family: inherit;
          font-size: 0.82rem;
          line-height: 1.6;
          color: var(--ink-2);
          background: transparent;
          outline: none;
          resize: none;
          padding: 0.25rem 0;
          box-sizing: border-box;
        }
      `}</style>

      {/* ── Main screen ── */}
      <div id="cp-screen">

        {/* Page header */}
        <div style={{ maxWidth: 1340, margin: '0 auto', padding: '0 22px' }}>
          <div className="ak-page-head" style={{ marginTop: 20 }}>
            <button
              className="ak-btn ak-btn--ghost ak-btn--sm"
              onClick={() => navigate(`/projects/${id}`)}
            >
              ← Project
            </button>
            <h1 className="t-page">Cost Proposal</h1>
            <span className="ak-page-sub">{project?.name}</span>
          </div>
        </div>

        {/* Body: two-column rail layout */}
        <div className="ak-app ak-app--rail" style={{ paddingTop: 0 }}>

          {/* ── Left: document (WYSIWYG) ── */}
          <div>

            {/* Hint banner */}
            {!hintDismissed && (
              <div
                className="ak-card"
                style={{ background: 'var(--green-soft)', marginBottom: 16, padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              >
                <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>
                  Click any line to show or hide it from the client
                </span>
                <button
                  className="ak-iconbtn"
                  onClick={() => {
                    localStorage.setItem('cp-hint-dismissed', '1')
                    setHintDismissed(true)
                  }}
                  style={{ fontSize: 16, lineHeight: 1 }}
                >
                  ×
                </button>
              </div>
            )}

            {/* Document card */}
            <div className="ak-card" style={{ padding: '28px 32px', fontFamily: 'inherit', fontSize: '0.85rem', lineHeight: '1.55', color: 'var(--ink)' }}>

              {/* Document header: trader info + meta */}
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '2rem', marginBottom: '1.5rem' }}>
                {/* Left: logo / trading name + contact */}
                <div style={{ flex: 1 }}>
                  {settings?.logo_url
                    ? <img src={settings.logo_url} alt="" style={{ maxHeight: 60, maxWidth: 160, objectFit: 'contain', display: 'block', marginBottom: '0.5rem' }} />
                    : <div style={{ fontFamily: 'var(--mono)', fontSize: '1rem', fontWeight: 600, marginBottom: '0.4rem' }}>{tradingName}</div>
                  }
                  <div style={{ fontSize: '0.74rem', color: 'var(--ink-3)', lineHeight: '1.7' }}>
                    {settings?.address && <div>{settings.address}</div>}
                    {settings?.phone && <div>{settings.phone}</div>}
                    {settings?.email && <div>{settings.email}</div>}
                  </div>
                </div>

                {/* Right: doc type + meta fields */}
                <div style={{ minWidth: 200 }}>
                  <div style={{ fontFamily: 'var(--mono)', fontSize: '1rem', fontWeight: 600, marginBottom: '0.6rem', textAlign: 'right' }}>
                    Cost Proposal
                  </div>
                  {/* Ref */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start', gap: '0.6rem', marginBottom: '0.18rem' }}>
                    <span style={{ fontFamily: 'var(--mono)', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--ink-3)', paddingTop: '0.2rem', flexShrink: 0 }}>Ref</span>
                    <input
                      className="cp-meta-input cp-editable"
                      value={quoteRef}
                      placeholder="—"
                      onChange={e => setQuoteRef(e.target.value)}
                    />
                  </div>
                  {/* Date */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start', gap: '0.6rem', marginBottom: '0.18rem' }}>
                    <span style={{ fontFamily: 'var(--mono)', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--ink-3)', paddingTop: '0.2rem', flexShrink: 0 }}>Date</span>
                    <input
                      className="cp-meta-input cp-editable"
                      type="date"
                      value={dateIssued}
                      onChange={e => setDateIssued(e.target.value)}
                    />
                  </div>
                  {/* For */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start', gap: '0.6rem', marginBottom: '0.18rem' }}>
                    <span style={{ fontFamily: 'var(--mono)', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--ink-3)', paddingTop: '0.2rem', flexShrink: 0 }}>For</span>
                    <input
                      className="cp-meta-input cp-editable"
                      value={project?.client_name || ''}
                      placeholder="Client name"
                      onChange={e => setProject(p => ({...p, client_name: e.target.value}))}
                      onBlur={e => updateProjectField('client_name', e.target.value)}
                    />
                  </div>
                  {/* Address */}
                  {(project?.client_address || true) && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start', gap: '0.6rem', marginBottom: '0.18rem' }}>
                      <span style={{ fontFamily: 'var(--mono)', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--ink-3)', paddingTop: '0.2rem', flexShrink: 0 }}>Address</span>
                      <textarea
                        className="cp-meta-textarea cp-editable"
                        value={project?.client_address || ''}
                        placeholder="Client address"
                        rows={2}
                        onChange={e => setProject(p => ({...p, client_address: e.target.value}))}
                        onBlur={e => updateProjectField('client_address', e.target.value)}
                      />
                    </div>
                  )}
                  {/* Project */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start', gap: '0.6rem', marginBottom: '0.18rem' }}>
                    <span style={{ fontFamily: 'var(--mono)', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--ink-3)', paddingTop: '0.2rem', flexShrink: 0 }}>Project</span>
                    <span style={{ fontSize: '0.82rem', textAlign: 'right' }}>{project?.name}</span>
                  </div>
                </div>
              </div>

              {/* Project description */}
              <textarea
                className="cp-description-area cp-editable"
                value={description}
                placeholder="Describe the scope of work…"
                onChange={e => setDescription(e.target.value)}
                rows={3}
              />

              <div style={{ borderTop: '1.5px solid var(--line-2)', margin: '0.75rem 0' }} />

              {/* Lines table header */}
              <div style={{ display: 'flex', fontFamily: 'var(--mono)', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.8px', color: 'var(--ink-3)', paddingBottom: '0.35rem', borderBottom: '1.5px solid var(--line-2)', marginBottom: '0.35rem' }}>
                <span style={{ flex: 1 }}>Description</span>
                <span style={{ width: 88, textAlign: 'right', flexShrink: 0 }}>Amount</span>
              </div>

              {/* Categories */}
              {cats.map(cat => {
                const items = catItemsMap(cat.id)
                const clientTotal = catClientTotal(cat.id)
                const allHid = allHidden(cat.id)
                const visible = visibleItems(cat.id)
                const hidden = hiddenItems(cat.id)
                const sundry = sundryClientTotal(cat.id)
                const mu = getMarkup(cat.id)
                const catColor = CAT_COLOR[cat.id] || 'var(--ink-3)'

                return (
                  <div key={cat.id} style={{ marginBottom: '0.5rem' }}>
                    {/* Category header row */}
                    <div style={{ display: 'flex', alignItems: 'center', padding: '0.32rem 0', borderTop: '1px solid var(--line)', userSelect: 'none' }}>
                      <span
                        className="ak-dot"
                        style={{ '--cat': catColor, marginRight: 8 }}
                      />
                      <span className="t-section" style={{ flex: 1, color: 'var(--ink)' }}>{cat.name}</span>
                      <span style={{ fontFamily: 'var(--mono)', fontSize: '0.82rem', fontWeight: 600, width: 88, textAlign: 'right', flexShrink: 0 }}>{gbp(clientTotal)}</span>
                    </div>

                    {/* Visible line items */}
                    {!allHid && (
                      <>
                        {visible.map(item => {
                          const cost = itemCost(item, cat.id)
                          const clientCost = cost * (1 + mu / 100)
                          const name = item.name || item.description
                          const qty = itemQtyLabel(item, cat.id)
                          return (
                            <div
                              key={item.id}
                              className="cp-line-row"
                              style={{ display: 'flex', alignItems: 'baseline', padding: '0.22rem 4px', borderBottom: '1px solid var(--line)', userSelect: 'none' }}
                              onClick={() => toggleItem(item)}
                              title="Click to hide from client"
                            >
                              <span style={{ flex: 1, fontSize: '0.82rem', color: 'var(--ink)' }}>{name}</span>
                              <span style={{ fontFamily: 'var(--mono)', fontSize: '0.68rem', color: 'var(--ink-3)', marginRight: '0.75rem', flexShrink: 0 }}>{qty}</span>
                              <span style={{ fontFamily: 'var(--mono)', fontSize: '0.82rem', width: 88, textAlign: 'right', flexShrink: 0, color: 'var(--ink)' }}>{gbp(clientCost)}</span>
                            </div>
                          )
                        })}
                        {sundry > 0 && (
                          <div style={{ display: 'flex', alignItems: 'baseline', padding: '0.22rem 4px', borderBottom: '1px solid var(--line)' }}>
                            <span style={{ flex: 1, fontSize: '0.82rem', color: 'var(--ink)' }}>Sundries</span>
                            <span style={{ fontFamily: 'var(--mono)', fontSize: '0.68rem', color: 'var(--ink-3)', marginRight: '0.75rem', flexShrink: 0 }}></span>
                            <span style={{ fontFamily: 'var(--mono)', fontSize: '0.82rem', width: 88, textAlign: 'right', flexShrink: 0, color: 'var(--ink)' }}>{gbp(sundry)}</span>
                          </div>
                        )}
                      </>
                    )}

                    {/* Hidden items — greyed, only shown if they have cost */}
                    {items.map(item => {
                      if (!isHidden(item)) return null
                      if (itemCost(item, cat.id) === 0) return null  // zero cost — don't show at all
                      const name = item.name || item.description
                      const qty = itemQtyLabel(item, cat.id)
                      return (
                        <div
                          key={item.id}
                          className="cp-line-row"
                          style={{ display: 'flex', alignItems: 'baseline', padding: '0.22rem 4px', borderBottom: '1px solid var(--line)', opacity: 0.32, userSelect: 'none' }}
                          onClick={() => toggleItem(item)}
                          title="Click to show to client"
                        >
                          <span style={{ flex: 1, fontSize: '0.82rem', color: 'var(--ink)' }}>{name}</span>
                          <span style={{ fontFamily: 'var(--mono)', fontSize: '0.68rem', color: 'var(--ink-3)', marginRight: '0.75rem', flexShrink: 0 }}>{qty}</span>
                          <span style={{ fontFamily: 'var(--mono)', fontSize: '0.82rem', width: 88, textAlign: 'right', flexShrink: 0, letterSpacing: '2px', color: 'var(--ink-3)' }}>···</span>
                        </div>
                      )
                    })}
                  </div>
                )
              })}

              <div style={{ borderTop: '1.5px solid var(--line-2)', margin: '0.75rem 0' }} />

              {/* Totals */}
              {vatAmt > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.2rem 0', fontSize: '0.85rem' }}>
                  <span>Subtotal</span>
                  <span style={{ fontFamily: 'var(--mono)' }}>{gbp(clientSubtotal)}</span>
                </div>
              )}
              {vatAmt > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.2rem 0', fontSize: '0.85rem' }}>
                  <span>VAT ({project?.vat_rate ?? 20}%)</span>
                  <span style={{ fontFamily: 'var(--mono)' }}>{gbp(vatAmt)}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.3rem 0', fontSize: '0.95rem', fontWeight: 600 }}>
                <span>{vatAmt > 0 ? 'Total inc. VAT' : 'Ex VAT'}</span>
                <span style={{ fontFamily: 'var(--mono)', fontWeight: 700 }}>{gbp(grandTotal)}</span>
              </div>

              <div style={{ borderTop: '1.5px solid var(--line-2)', margin: '0.75rem 0' }} />

              {/* Terms & Conditions */}
              <div style={{ marginBottom: '1rem' }}>
                <div className="t-label" style={{ marginBottom: '0.3rem', paddingBottom: '0.18rem', borderBottom: '1px solid var(--line)' }}>
                  Terms &amp; Conditions
                </div>
                <textarea
                  className="cp-text-area cp-editable"
                  value={paymentTerms}
                  placeholder="Payment terms, conditions, validity period…"
                  onChange={e => setPaymentTerms(e.target.value)}
                  rows={4}
                />
              </div>

              {/* Additional notes */}
              <div style={{ marginBottom: '1rem' }}>
                <div className="t-label" style={{ marginBottom: '0.3rem', paddingBottom: '0.18rem', borderBottom: '1px solid var(--line)' }}>
                  Additional Notes
                </div>
                <textarea
                  className="cp-text-area cp-editable"
                  value={quoteNotes}
                  placeholder="Exclusions, assumptions, access requirements…"
                  onChange={e => setQuoteNotes(e.target.value)}
                  rows={3}
                />
              </div>

              <div style={{ textAlign: 'right', fontFamily: 'var(--mono)', fontSize: '0.55rem', color: 'var(--line-2)', marginTop: '2rem', letterSpacing: '1px', textTransform: 'uppercase' }}>
                Produced with Akkord
              </div>
            </div>
          </div>

          {/* ── Right column: sticky rail ── */}
          <div className="ak-rail-col">
            <div className="ak-rail">
              <div className="ak-rail-title">Quote Summary</div>

              {/* Per-category subtotals */}
              <div className="ak-rail-group">
                {cats.map(cat => {
                  const catColor = CAT_COLOR[cat.id] || 'var(--ink-3)'
                  return (
                    <div key={cat.id} className="ak-rail-row">
                      <span className="k" style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                        <span className="ak-dot" style={{ '--cat': catColor }} />
                        {cat.name}
                      </span>
                      <span className="v">{gbp(catClientTotal(cat.id))}</span>
                    </div>
                  )
                })}
                <div className="ak-rail-row sub">
                  <span className="k">Subtotal</span>
                  <span className="v">{gbp(clientSubtotal)}</span>
                </div>
                {vatAmt > 0 && (
                  <div className="ak-rail-row">
                    <span className="k">VAT ({project?.vat_rate ?? 20}%)</span>
                    <span className="v">{gbp(vatAmt)}</span>
                  </div>
                )}
              </div>

              {/* Grand total */}
              <div className="ak-rail-total">
                <div className="l">{vatAmt > 0 ? 'Total inc. VAT' : 'Ex VAT'}</div>
                <div className="t-figure">{gbp(grandTotal)}</div>
              </div>

              {/* Markup panel */}
              <div style={{ borderTop: '1px solid var(--line)', padding: '14px 18px 4px' }}>
                <MarkupPanel
                  project={project}
                  onUpdate={updateMarkup}
                />
              </div>

              {/* Action buttons */}
              <div className="ak-rail-actions">
                <button
                  className="ak-btn ak-btn--primary ak-btn--block"
                  onClick={saveQuote}
                  disabled={saving}
                >
                  {saving ? 'Saving…' : savedMsg ? '✓ Saved' : 'Save quote'}
                </button>
                <button
                  className="ak-btn ak-btn--dark ak-btn--block"
                  onClick={printPdf}
                >
                  Generate PDF / Print
                </button>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* ── Print-only document ── */}
      <div id="cp-print">
        <PrintDoc
          project={project}
          settings={settings}
          cats={cats}
          catItemsMap={catItemsMap}
          itemCost={itemCost}
          itemQtyLabel={itemQtyLabel}
          catClientTotal={catClientTotal}
          getMarkup={getMarkup}
          visibleItems={visibleItems}
          hiddenItems={hiddenItems}
          allHidden={allHidden}
          sundryClientTotal={sundryClientTotal}
          clientSubtotal={clientSubtotal}
          vatAmt={vatAmt}
          grandTotal={grandTotal}
          dateIssued={dateIssued}
          quoteRef={quoteRef}
          description={description}
          paymentTerms={paymentTerms}
          quoteNotes={quoteNotes}
        />
      </div>

      {/* ── Saved toast ── */}
      <div className={`ak-toast${savedMsg ? ' show' : ''}`}>
        <span className="ok">✓</span> Quote saved
      </div>
    </>
  )
}

// ── Print document — only shown items, no UI chrome ───────────────────────────
function PrintDoc({
  project, settings, cats, catItemsMap, itemCost, itemQtyLabel,
  catClientTotal, getMarkup, visibleItems, hiddenItems, allHidden,
  sundryClientTotal, clientSubtotal, vatAmt, grandTotal,
  dateIssued, quoteRef, description, paymentTerms, quoteNotes
}) {
  return (
    <div style={p.root}>
      <div style={p.header}>
        <div>
          {settings?.logo_url
            ? <img src={settings.logo_url} alt="" style={p.logo} />
            : <div style={p.tradingName}>{settings?.trading_name || ''}</div>
          }
          <div style={p.traderDetails}>
            {settings?.address && <div>{settings.address}</div>}
            {settings?.phone && <div>{settings.phone}</div>}
            {settings?.email && <div>{settings.email}</div>}
            {settings?.company_number && <div>Co. No. {settings.company_number}</div>}
            {project?.vat_registered && settings?.vat_number && <div>VAT No. {settings.vat_number}</div>}
          </div>
        </div>
        <div style={p.docMeta}>
          <div style={p.docTitle}>Cost Proposal</div>
          {quoteRef && <div style={p.metaRow}><span style={p.metaKey}>Ref</span><span>{quoteRef}</span></div>}
          <div style={p.metaRow}><span style={p.metaKey}>Date</span><span>{formatDate(dateIssued)}</span></div>
          {project?.client_name && <div style={p.metaRow}><span style={p.metaKey}>For</span><span>{project.client_name}</span></div>}
          {project?.client_address && <div style={p.metaRow}><span style={p.metaKey}>Address</span><span style={{ whiteSpace: 'pre-line' }}>{project.client_address}</span></div>}
          {project?.name && <div style={p.metaRow}><span style={p.metaKey}>Project</span><span>{project.name}</span></div>}
        </div>
      </div>

      {description && <div style={p.description}>{description}</div>}

      <div style={p.divider} />
      <div style={p.linesHead}><span style={{ flex: 1 }}>Description</span><span style={p.colAmt}>Amount</span></div>

      {cats.map(cat => {
        const mu = getMarkup(cat.id)
        const clientTotal = catClientTotal(cat.id)
        const allHid = allHidden(cat.id)
        const visible = visibleItems(cat.id)
        const sundry = sundryClientTotal(cat.id)

        return (
          <div key={cat.id} style={p.catBlock}>
            <div style={p.catRow}>
              <span style={{ flex: 1 }}>{cat.name}</span>
              <span style={p.colAmt}>{fmt(clientTotal)}</span>
            </div>
            {!allHid && (
              <>
                {visible.map(item => {
                  const cost = itemCost(item, cat.id)
                  const clientCost = cost * (1 + mu / 100)
                  const name = item.name || item.description
                  const qty = itemQtyLabel(item, cat.id)
                  return (
                    <div key={item.id} style={p.lineRow}>
                      <span style={{ flex: 1 }}>{name}</span>
                      <span style={p.lineQty}>{qty}</span>
                      <span style={p.colAmt}>{fmt(clientCost)}</span>
                    </div>
                  )
                })}
                {sundry > 0 && (
                  <div style={p.lineRow}>
                    <span style={{ flex: 1 }}>Sundries</span>
                    <span style={p.lineQty}></span>
                    <span style={p.colAmt}>{fmt(sundry)}</span>
                  </div>
                )}
              </>
            )}
          </div>
        )
      })}

      <div style={p.divider} />
      {vatAmt > 0 && <>
        <div style={p.totalRow}><span>Subtotal</span><span>{fmt(clientSubtotal)}</span></div>
        <div style={p.totalRow}><span>VAT ({project?.vat_rate ?? 20}%)</span><span>{fmt(vatAmt)}</span></div>
      </>}
      <div style={p.grandRow}><span>{vatAmt > 0 ? 'Total inc. VAT' : 'Ex VAT'}</span><span>{fmt(grandTotal)}</span></div>
      {paymentTerms && <div style={p.section}><div style={p.sectionTitle}>Terms &amp; Conditions</div><div style={p.sectionBody}>{paymentTerms}</div></div>}
      {quoteNotes && <div style={p.section}><div style={p.sectionTitle}>Additional Notes</div><div style={p.sectionBody}>{quoteNotes}</div></div>}
      <div style={p.brand}>Produced with Akkord</div>
    </div>
  )
}

// ── Print styles (used by PrintDoc only — never the screen UI) ────────────────
const p = {
  root: { fontFamily: '"Hanken Grotesk", system-ui, sans-serif', color: '#1a1a1a', fontSize: '0.85rem', lineHeight: '1.5', padding: '0' },
  header: { display: 'flex', justifyContent: 'space-between', gap: '2rem', marginBottom: '1.5rem' },
  logo: { maxHeight: '60px', maxWidth: '160px', objectFit: 'contain', display: 'block', marginBottom: '0.4rem' },
  tradingName: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '1rem', fontWeight: 600, marginBottom: '0.4rem' },
  traderDetails: { fontSize: '0.74rem', color: '#666', lineHeight: '1.7' },
  docMeta: { textAlign: 'right', minWidth: '180px' },
  docTitle: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '1rem', fontWeight: 600, marginBottom: '0.6rem' },
  metaRow: { display: 'flex', justifyContent: 'flex-end', gap: '0.6rem', fontSize: '0.78rem', marginBottom: '0.15rem' },
  metaKey: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#888' },
  description: { fontSize: '0.85rem', color: '#444', lineHeight: '1.6', marginBottom: '1rem', whiteSpace: 'pre-line' },
  divider: { borderTop: '1.5px solid #e0dcd6', margin: '0.75rem 0' },
  linesHead: { display: 'flex', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.8px', color: '#888', paddingBottom: '0.35rem', borderBottom: '1.5px solid #e0dcd6', marginBottom: '0.35rem' },
  colAmt: { width: '88px', textAlign: 'right', flexShrink: 0, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' },
  catBlock: { marginBottom: '0.5rem' },
  catRow: { display: 'flex', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.72rem', fontWeight: 600, padding: '0.32rem 0', borderTop: '1px solid #e8e4df', textTransform: 'uppercase', letterSpacing: '0.5px' },
  lineRow: { display: 'flex', alignItems: 'baseline', padding: '0.22rem 0', borderBottom: '1px solid #f3f0eb' },
  lineQty: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.68rem', color: '#888', marginRight: '0.75rem', flexShrink: 0 },
  totalRow: { display: 'flex', justifyContent: 'space-between', padding: '0.2rem 0', fontSize: '0.85rem' },
  grandRow: { display: 'flex', justifyContent: 'space-between', padding: '0.3rem 0', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontWeight: 700, fontSize: '0.95rem' },
  vatNote: { fontSize: '0.72rem', color: '#888', fontStyle: 'italic', marginTop: '0.5rem' },
  section: { marginTop: '1.25rem' },
  sectionTitle: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.8px', color: '#888', marginBottom: '0.3rem', paddingBottom: '0.18rem', borderBottom: '1px solid #e8e4df' },
  sectionBody: { fontSize: '0.82rem', color: '#444', lineHeight: '1.6', whiteSpace: 'pre-line' },
  brand: { textAlign: 'right', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.55rem', color: '#ccc', marginTop: '1.5rem', letterSpacing: '1px', textTransform: 'uppercase' },
}
